import type { AxiosProgressEvent } from "axios";
import axios from "axios";
import type { UUID } from "@/Shared/types";

// Even after resize a photo on weak cellular can take a minute+; allow plenty
// of headroom so we only retry on genuine drops, not slow links.
const UPLOAD_TIMEOUT_MS = 300_000;
const MAX_RETRIES = 3;

function isRetryableError(e: unknown): boolean {
  if (!axios.isAxiosError(e)) return false;
  // Retry on network errors (no response received) and timeouts. Don't retry
  // on 4xx/5xx — those mean the server received the request and rejected it.
  return !e.response || e.code === "ERR_NETWORK" || e.code === "ECONNABORTED";
}

export class PhotosApi {
  baseUrl: string;
  accessToken: string;

  constructor(baseUrl: string, accessToken: string) {
    this.baseUrl = baseUrl;
    this.accessToken = accessToken;
  }

  async uploadPhotoWithTrackingId(
    trackingId: UUID,
    blob: Blob,
    filename: string,
    onUploadProgress?: (percentComplete: number) => void,
  ): Promise<void> {
    const formData = new FormData();
    formData.append("file0", blob, filename);

    const url = `${this.baseUrl}/photos/upload-photo-with-tracking-id?trackingId=${trackingId}`;

    const options = {
      headers: {
        Authorization: `Bearer ${this.accessToken}`,
        "Content-Type": "multipart/form-data",
      },
      timeout: UPLOAD_TIMEOUT_MS,
      ...(onUploadProgress
        ? {
            onUploadProgress: (event: AxiosProgressEvent) => {
              if (event.total && event.total > 0) {
                onUploadProgress(
                  Math.round((event.loaded / event.total) * 100),
                );
              }
            },
          }
        : {}),
    };

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      try {
        await axios.post(url, formData, options);
        return;
      } catch (e) {
        const isLastAttempt = attempt === MAX_RETRIES;

        if (isLastAttempt || !isRetryableError(e)) {
          throw e;
        }

        console.warn(
          `Photo upload attempt ${attempt} failed, retrying...`,
          axios.isAxiosError(e)
            ? { code: e.code, message: e.message }
            : undefined,
        );

        await new Promise((resolve) =>
          setTimeout(resolve, 2000 * Math.pow(2, attempt - 1)),
        );

        onUploadProgress?.(0);
      }
    }
  }
}
